export type CharacterRarity = "COMMON" | "RARE" | "EPIC";

export interface CharacterDef {
  id: string;
  name: string;
  rarity: CharacterRarity;
  priceGems: number; // 0 = free/default
  color: string; // Theme color for UI accents (deterministic BG from palette)
  icon: string; // Single character fallback
  imagePath: string; // Path to full character portrait PNG
  imageSmallPath: string; // Path to small character badge PNG (same file for 500 skins)
}

// ── 16-color background palette ──────────────────────────────────────────
export const BG_PALETTE = [
  "#FF6B6B", "#FF8E53", "#FECA57", "#48DBFB",
  "#0ABDE3", "#10AC84", "#1DD1A1", "#54A0FF",
  "#5F27CD", "#8854D0", "#FC427B", "#F8B739",
  "#6D214F", "#182C61", "#2C3A47", "#B33771",
];

// ── Generate shop skins (8118-13000, above NFT-exclusive range) ──────────
const SHOP_SKIN_START = 8118;
const SHOP_SKIN_END = 13000;
const SHOP_SKIN_COUNT = SHOP_SKIN_END - SHOP_SKIN_START + 1; // 4883

// Images live in Supabase Storage bucket "characters"
const CHARACTER_STORAGE_BASE = import.meta.env.VITE_CHARACTER_STORAGE_BASE ?? "https://svzlccqnnyhrhozrfnxj.supabase.co/storage/v1/object/public/characters";

function buildCatalog(): CharacterDef[] {
  const catalog: CharacterDef[] = [];
  for (let i = 0; i < SHOP_SKIN_COUNT; i++) {
    const skinNum = SHOP_SKIN_START + i;
    const id = `skin_${String(skinNum).padStart(5, "0")}`;
    const name = `Skin #${skinNum}`;
    const color = BG_PALETTE[skinNum % 16];

    // Distribute rarities across the range
    let rarity: CharacterRarity;
    let priceGems: number;
    const pct = i / SHOP_SKIN_COUNT;
    if (pct < 0.5) {
      rarity = "COMMON";
      priceGems = 25;
    } else if (pct < 0.85) {
      rarity = "RARE";
      priceGems = 75;
    } else {
      rarity = "EPIC";
      priceGems = 200;
    }

    const path = `${CHARACTER_STORAGE_BASE}/${skinNum}.png`;
    catalog.push({
      id,
      name,
      rarity,
      priceGems,
      color,
      icon: String(skinNum % 10),
      imagePath: path,
      imageSmallPath: path,
    });
  }
  return catalog;
}

export const CHARACTER_CATALOG: readonly CharacterDef[] = buildCatalog();

// ── Legacy migration map (old 4 hardcoded IDs → new skin IDs) ───────────
const LEGACY_MAP: Record<string, string> = {
  beast_starter: "skin_08118",
  pixel_panda: "skin_08119",
  blaze_fox: "skin_08120",
  neon_wolf: "skin_08121",
};

export function migrateLegacyId(id: string): string {
  return LEGACY_MAP[id] ?? id;
}

export function isLegacyId(id: string): boolean {
  return id in LEGACY_MAP;
}

// ── Lookups ──────────────────────────────────────────────────────────────
const catalogById = new Map<string, CharacterDef>();
for (const c of CHARACTER_CATALOG) {
  catalogById.set(c.id, c);
}
// Also index by legacy IDs so old references resolve
for (const [legacyId, newId] of Object.entries(LEGACY_MAP)) {
  const def = catalogById.get(newId);
  if (def != null) catalogById.set(legacyId, def);
}
// Special NPC skin — equippable by user "rafsby"
catalogById.set("rafsby", {
  id: "rafsby",
  name: "Rafsby",
  rarity: "EPIC",
  priceGems: 0,
  color: "#ffd700",
  icon: "R",
  imagePath: "/assets/icons/rafsby-portrait.png",
  imageSmallPath: "/assets/icons/rafsby-portrait.png",
});

// Also index by bare numeric IDs ("1" → skin_001, "2" → skin_002, etc.)
for (const c of CHARACTER_CATALOG) {
  const numMatch = c.id.match(/^skin_(\d+)$/);
  if (numMatch) {
    catalogById.set(String(Number(numMatch[1])), c);
  }
}

export function getCharacterById(id: string): CharacterDef | undefined {
  return catalogById.get(id);
}

/** Resolve image path for any skin ID, even if not in shop catalog (e.g. NFT tokens 1-8117). */
export function resolveCharacterImagePath(skinId: string): string {
  const def = catalogById.get(skinId);
  if (def) return def.imageSmallPath;
  // Extract numeric part: "skin_00042" → 42, "42" → 42
  const numMatch = skinId.match(/(\d+)/);
  const num = numMatch ? Number(numMatch[1]) : 0;
  return `${CHARACTER_STORAGE_BASE}/${num || 8118}.png`;
}

export function getShopCharacters(): CharacterDef[] {
  return CHARACTER_CATALOG.filter((c) => c.priceGems > 0);
}

export function getDefaultCharacter(): CharacterDef {
  return CHARACTER_CATALOG[0];
}

// ── Daily shop rotation (6 skins, same for all players on the same UTC day) ──
export function getDailyShopSkins(
  nowMs: number = Date.now(),
  _ownedIds: readonly string[] = [],
): CharacterDef[] {
  const daySeed = Math.floor(nowMs / 86_400_000);
  // Simple seeded shuffle: use xorshift-style mixing with daySeed
  const purchasable = CHARACTER_CATALOG.filter((c) => c.priceGems > 0);
  const shuffled = [...purchasable];
  let s = daySeed;
  for (let i = shuffled.length - 1; i > 0; i--) {
    s = ((s * 1103515245 + 12345) >>> 0) & 0x7fffffff;
    const j = s % (i + 1);
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  // Same 6 skins all day — owned ones show "OWNED" in the shop UI
  return shuffled.slice(0, 6);
}

// ── Skin background color helper ─────────────────────────────────────────
export function getSkinBgColor(skinId: string): string {
  const idx = parseInt(skinId.replace("skin_", ""), 10);
  if (isNaN(idx)) return BG_PALETTE[0];
  return BG_PALETTE[idx % 16];
}

export const RARITY_STARS: Record<CharacterRarity, number> = {
  COMMON: 1,
  RARE: 2,
  EPIC: 3,
};

export const RARITY_COLORS: Record<CharacterRarity, string> = {
  COMMON: "#aaaaaa",
  RARE: "#5c8cd4",
  EPIC: "#b44aff",
};

export interface GemPackDef {
  gems: number;
  coinCost: number;
  label: string;
  imagePath: string;
}

export const GEM_PACKS: readonly GemPackDef[] = [
  { gems: 5, coinCost: 450, label: "5 GEMS", imagePath: "/assets/icons/gem-pack-5.png" },
  { gems: 25, coinCost: 1750, label: "25 GEMS", imagePath: "/assets/icons/gem-pack-25.png" },
  { gems: 50, coinCost: 3000, label: "50 GEMS", imagePath: "/assets/icons/gem-pack-50.png" },
  { gems: 100, coinCost: 5000, label: "100 GEMS", imagePath: "/assets/icons/gem-pack-100.png" },
] as const;
